import { TabularTypeAdapter } from 'app/model/adapters/dataflow/formatAdapters/tabular-type.adapter';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned } from 'shell-models-lib';
import { Microsoft365Settings } from 'app/model/dataflow/dataset-settings/microsoft365-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { office365UserScopes } from 'app/registries/consts/metadata-consts';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import {
    ArrayDataFlowType,
    BooleanDataFlowType,
    StringDataFlowType,
    TimeStampDataFlowType
} from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(Microsoft365Settings)
})
export class Microsoft365Adapter extends TabularTypeAdapter {
    public override async toSourceDSL(setting: Microsoft365Settings): Promise<string[]> {
        const props: string[] = [];
        props.push(`store: '${DatasetFormatType.Microsoft365}'`);
        props.push(`format: '${DatasetFormatType.Json}'`);
        props.push(`sourceTableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        props.push(`autoFlatten: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.autoFlatten, BooleanDataFlowType.TYPE)}`);
        if (setting.userScope === office365UserScopes.group) {
            if (setting.allowedGroups.expression || setting.allowedGroups.expressions.length) {
                props.push(`sourceAllowedGroups: ${DataFlowExpressionUtils.wrapExpressionArrayConfigValue(setting.allowedGroups)}`);
            }
        } else {
            if (setting.userScopeFilterUri.expression) {
                props.push(`sourceScopeFilter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.userScopeFilterUri)}`);
            }
        }
        if (setting.dateFilterColumn.expression) {
            const dateFilterCol = DataFlowExpressionUtils.wrapExpressionConfigValue(setting.dateFilterColumn);
            if (dateFilterCol && dateFilterCol.length > 0 && dateFilterCol !== 'Default') {
                props.push(`sourceDateFilterColumn: ${dateFilterCol}`);
            }
        }
        if (setting.startTime.expression) {
            props.push(`sourceStartTime: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.startTime, TimeStampDataFlowType.TYPE)}`);
        }
        if (setting.endTime.expression) {
            props.push(`sourceEndTime: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.endTime, TimeStampDataFlowType.TYPE)}`);
        }
        if (setting.structure.expressions.length || setting.structure.expression) {
            if (setting.structure.templateExpression) {
                props.push(`sourceStructure: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.structure, ArrayDataFlowType.TYPE)}`);
            } else {
                props.push(`sourceStructure: [\n\t\t${
                    setting.structure.expressions.map(
                        (expression) => `['name' -> '${expression.name}', 'type' -> '${expression.columnType}']`
                    ).join(',\n\t\t')
                }\n\t]`);
            }
        }
        return props;
    }

    public override async toSourceModel(owner: DataFlowSource, configs: Map<string, string>, handledConfigs: string[]): Promise<Microsoft365Settings> {
        const setting = this._generateSetting(owner);
        if (configs.has('sourceTableName')) {
            setting.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('sourceTableName'), owner);
        }
        if (configs.has('autoFlatten')) {
            setting.autoFlatten = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('autoFlatten'), owner, BooleanDataFlowType.TYPE);
        }
        if (configs.has('sourceAllowedGroups')) {
            setting.allowedGroups = DataFlowExpressionUtils.unwrapExpressionArrayConfigValue(configs.get('sourceAllowedGroups'), owner, StringDataFlowType.TYPE);
            setting.userScope = office365UserScopes.group;
        }
        if (configs.has('sourceScopeFilter')) {
            setting.userScopeFilterUri = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('sourceScopeFilter'), owner);
            setting.userScope = office365UserScopes.all;
        }
        if (configs.has('sourceDateFilterColumn')) {
            setting.dateFilterColumn = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('sourceDateFilterColumn'), owner);
        }
        if (configs.has('sourceStartTime')) {
            setting.startTime = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('sourceStartTime'), owner, TimeStampDataFlowType.TYPE);
        }
        if (configs.has('sourceEndTime')) {
            setting.endTime = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('sourceEndTime'), owner, TimeStampDataFlowType.TYPE);
        }
        if (configs.has('sourceStructure')) {
            const expression = configs.get('sourceStructure');
            if (expression.startsWith('(') && expression.endsWith(')')) {
                const value = DataFlowExpressionUtils.parseNextValueFromADsl({ value: expression.trim() }, true);
                setting.structure.templateExpression = value;
                setting.structure.expression = value;
            } else {
                DataFlowExpressionUtils
                    .parseScopeArrayInDsl(expression)
                    .map((m) => DataFlowExpressionUtils.parseStringMapInDsl(m))
                    .forEach((map: Map<`'name'` | `'type'`, string>) => {
                        const dataflowColumn = Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, owner);
                        dataflowColumn.name = DataFlowExpressionUtils.unwrapExpressionConfigValue(map.get(`'name'`), owner).expression;
                        dataflowColumn.columnType = DataFlowExpressionUtils.unwrapExpressionConfigValue(map.get(`'type'`), owner).expression;
                        setting.structure.expressions.push(dataflowColumn);
                    });
            }
        }
        return setting;
    }

    public override toSinkDSL(): never {
        throw new Error(`${DatasetFormatType.Microsoft365} is only supported in dataflow source.`);
    }

    public override toSinkModel(): never {
        throw new Error(`${DatasetFormatType.Microsoft365} is only supported in dataflow source.`);
    }

    protected override _generateSetting(owner: DataFlowSource | DataFlowSink): Microsoft365Settings {
        const result = Owned.create<Microsoft365Settings>(Microsoft365Settings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
